/**
 * LockedTaskField — kilitli görev alanları için "neden disable?" tooltip'i.
 *
 * Kullanım:
 *   <LockedTaskField locked={!canEdit} reason="complete">
 *     <Checkbox disabled={!canEdit} ... />
 *   </LockedTaskField>
 *
 * `locked=false` iken children şeffaf geçer, sarmalama etkisiz.
 * `locked=true` iken:
 *   1) Hover'da açıklayıcı tooltip
 *   2) Küçük Lock ikonu overlay'i (opsiyonel, `showBadge` ile)
 */
import { useState } from "react";
import { Lock, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

const REASONS = {
  complete: (
    <>
      <b>Bu görevi tamamlayamazsın</b>
      <div className="mt-0.5 text-[11px] leading-snug opacity-90">
        Bir görevi yalnızca kendi oluşturduğun ya da sana atanmış olduğu
        durumda kapatabilirsin. Süper / Ultra yönetici ise tüm görevleri
        kapatabilir.
      </div>
    </>
  ),
  assign: (
    <>
      <b>Kişi atayamazsın</b>
      <div className="mt-0.5 text-[11px] leading-snug opacity-90">
        Görevi (yeniden) atamak için ya kendi oluşturduğun ya da mevcut
        atanan sen olmalısın. Süper / Ultra yönetici bypass eder.
      </div>
    </>
  ),
  reschedule: (
    <>
      <b>Tarih değiştiremezsin</b>
      <div className="mt-0.5 text-[11px] leading-snug opacity-90">
        Görev tarihini değiştirmek için ya kendi oluşturduğun ya da sana
        atanmış olmalı. Süper / Ultra yönetici bypass eder.
      </div>
    </>
  ),
  edit: (
    <>
      <b>Sadece görüntüleme</b>
      <div className="mt-0.5 text-[11px] leading-snug opacity-90">
        Bu görev sana atanmamış ve senin oluşturduğun bir görev değil.
        Yalnızca kendi görevlerinde değişiklik yapabilirsin.
      </div>
    </>
  ),
};

export function LockedTaskField({
  locked,
  reason = "edit",
  showBadge = false,
  taskId,
  children,
}) {
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  if (!locked) return children;
  const content = REASONS[reason] || REASONS.edit;

  const requestAssignment = async () => {
    if (!taskId || requesting || requested) return;
    setRequesting(true);
    try {
      await api.post(`/tasks/${taskId}/request-assignment`);
      setRequested(true);
      toast.success(
        "Talebin gönderildi — göreve atanan kişi ve oluşturana bildirim iletildi.",
      );
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setRequested(true);
        toast.info(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Son 24 saat içinde zaten talep gönderilmiş.",
        );
      } else {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Talep gönderilemedi",
        );
      }
    } finally {
      setRequesting(false);
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="relative inline-flex">
            {children}
            {showBadge && (
              <span
                className="pointer-events-none absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-zinc-500 text-white shadow-sm"
                aria-hidden
              >
                <Lock className="h-2 w-2" strokeWidth={3} />
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[260px] bg-zinc-900 p-3 text-white"
        >
          {content}
          {taskId && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                requestAssignment();
              }}
              disabled={requesting || requested}
              data-testid={`task-request-assignment-${taskId}`}
              className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md border border-white/20 bg-white/10 px-2 py-1 text-[11px] font-medium transition-colors hover:bg-white/20 disabled:opacity-50"
            >
              {requesting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Send className="h-3 w-3" />
              )}
              {requested
                ? "Talep gönderildi ✓"
                : "→ Görevi bana ata iste"}
            </button>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default LockedTaskField;
